import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import RoomBreakdownDetails from './RoomBreakdownDetails'
import RoomBreakdownLoadingSkeleton from './RoomBreakdownLoadingSkeleton'
import useOfferMetaData from '../../../../hooks/Offers/useOfferMetaData'
import { isBedbankOffer, isBedbankSearchRoom, isLEOffer, isReservationSearchRoom, isVillaOffer } from 'lib/offer/offerTypes'
import { getMaxNonInfantGuestCount } from 'lib/offer/occupancyUtils'
import { isAnytimeSearch, shouldHideSearchTileRoomBreakdown, shouldHideSearchTileRoomRecommendation } from './searchTileRoomBreakdownUtils'
import useBedbankRoomBreakdown from '../../../../hooks/useBedbankRoomBreakdown'
import { getRoomBreakdown, mapSearchOccupancyToOccupants } from 'lib/search/searchUtils'
import { excludeNullOrUndefined } from 'checkout/utils'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary | App.VillaOffer | App.VillaOfferSummary
  filters?: App.OfferListFilters
  isCroCompact?: boolean
}

function SearchTileRoomBreakdown({
  offer,
  filters,
  isCroCompact = false,
}: Props) {
  const intl = useIntl()
  const metaData = useOfferMetaData(offer.id, filters)
  const isSingleUnit = isVillaOffer(offer) || offer.holidayTypes?.includes('Single Unit')

  const leRoomsList = useMemo(() => {
    const packages = isLEOffer(offer) ? offer.packages : []
    const metaDataRooms = metaData?.rooms?.filter(isReservationSearchRoom) ?? []
    return getRoomBreakdown(metaDataRooms, packages)
  }, [metaData?.rooms, offer])

  const bedbankRooms = useMemo(() =>
    metaData?.rooms
      ?.filter(isBedbankSearchRoom)
      .map(r => (r.rateId ?
          {
            rateId: r.rateId,
            propertyId: r.propertyId,
            occupancy: mapSearchOccupancyToOccupants(r.occupancy),
          }
        : undefined))
      .filter(excludeNullOrUndefined) ?? [],
  [metaData?.rooms])

  const { rooms: bedbankRoomsList, isLoading: isBedbankLoading } = useBedbankRoomBreakdown(bedbankRooms, filters)

  // For single-unit properties, derive capacity from rates if available, otherwise fall back to static package data (e.g. anytime searches where no rates are fetched)
  const entireHome = useMemo(() => {
    if (!isSingleUnit) return undefined

    const bedbankPackage = isBedbankOffer(offer) ? offer.packages[0] : undefined
    const bedbankTotalGuests = bedbankPackage?.capacities?.combinations?.length
      ? Math.max(...bedbankPackage.capacities.combinations.map(c => c.adults + c.children + c.teenagers))
      : undefined

    const villaPackage = isLEOffer(offer) ? offer.lowestPricePackage : undefined
    const villaTotalGuests = villaPackage ? getMaxNonInfantGuestCount(villaPackage.roomRate?.capacities ?? []) : undefined

    const totalGuests = bedbankRoomsList?.[0]?.totalGuests ?? leRoomsList?.[0]?.totalGuests ?? bedbankTotalGuests ?? villaTotalGuests
    const numberOfBedrooms = bedbankRoomsList?.[0]?.numberOfBedrooms ?? leRoomsList?.[0]?.numberOfBedrooms ?? bedbankPackage?.numberOfBedrooms ?? villaPackage?.roomType?.numberOfBedrooms
    const numberOfBathrooms = bedbankRoomsList?.[0]?.numberOfBathrooms ?? leRoomsList?.[0]?.numberOfBathrooms ?? bedbankPackage?.numberOfBathrooms ?? villaPackage?.roomType?.numberOfBathrooms

    if (totalGuests == null || numberOfBedrooms == null || numberOfBathrooms == null) return undefined

    return { numberOfGuests: totalGuests, numberOfBedrooms, numberOfBathrooms }
  }, [isSingleUnit, bedbankRoomsList, leRoomsList, offer])

  const shouldHideRoomBreakdown = useMemo(() =>
    shouldHideSearchTileRoomBreakdown({ offer, filters, isEntireHome: !!entireHome }),
  [offer, filters, entireHome])

  const allRooms = useMemo(() =>
    [...leRoomsList, ...bedbankRoomsList],
  [leRoomsList, bedbankRoomsList])

  const recommendedForText = useMemo(() => {
    const numberOfAdults = filters?.rooms?.reduce((sum, room) => sum + room.adults, 0) ?? 0
    const numberOfChildren = filters?.rooms?.reduce((sum, room) => sum + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0), 0) ?? 0

    if (numberOfChildren > 0) {
      return intl.formatMessage({
        id: 'nNcfV-DXzQ7EvoRq9A0Z',
        defaultMessage: 'Recommended for {adultCountLabel}, {childCountLabel}',
      }, {
        adultCountLabel: intl.formatMessage({
          id: 'dtUpui-dwA565d1BXOqc',
          defaultMessage: '{numberOfAdults, plural, one {# adult} other {# adults}}',
        }, { numberOfAdults }),
        childCountLabel: intl.formatMessage({
          id: '1YxxSZKkrCPh0jwjaULr',
          defaultMessage: '{numberOfChildren, plural, one {# child} other {# children}}',
        }, { numberOfChildren }),
      })
    }

    return intl.formatMessage({
      id: 'IIz6owQbsG-icd70nlCS',
      defaultMessage: 'Recommended for {numberOfAdults, plural, one {# adult} other {# adults}}',
    }, { numberOfAdults })
  }, [filters?.rooms, intl])

  if (shouldHideRoomBreakdown) return null

  if (isBedbankLoading) return <RoomBreakdownLoadingSkeleton />

  if (allRooms.length === 0 && !entireHome) return null

  const isAnytime = isAnytimeSearch(filters)
  const showRecommendation = !isAnytime &&
    metaData?.available !== false &&
    !shouldHideSearchTileRoomRecommendation({ filters, isCroCompact })

  return (
    <RoomBreakdownDetails
      recommendedForText={recommendedForText}
      showRecommendation={showRecommendation}
      rooms={allRooms}
      entireHome={entireHome}
    />
  )
}

export default SearchTileRoomBreakdown
